import React, { useState } from 'react';
import {
    Button, Grid, TextField, Typography, CircularProgress
} from '@mui/material';
import { AccountBalance as CashIcon } from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader, { capitalizeFirst, formatPrintDay } from '../components/PrintAgencyHeader';
import { academyApi } from '../services/academyApi';
import { useLanguage } from '../../Commun/contexts/LanguageContext';

// boubaker 23/08/2026 - Page d'impression : Rapport de Caisse Journalier
// (GenericPage type 'print' : filtres masquables + impression de la zone résultats)
export const PrintDailyCashPage: React.FC = () => {
    const { t } = useLanguage();

    const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));

    const [rows, setRows] = useState<any[]>([]);
    const [totals, setTotals] = useState<Record<string, number>>({ cash: 0, card: 0, transfer: 0, grand: 0, count: 0 });
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleLoad = async () => {
        setLoading(true);
        try {
            // boubaker 23/08/2026 - chargement serveur dédié impression (DLL Blobtic_Print : totaux calculés côté SQL/C#)
            const res = await academyApi.getPrintDailyCash(reportDate);
            setRows(res?.payments || []);
            setTotals(res?.totals || { cash: 0, card: 0, transfer: 0, grand: 0, count: 0 });
            setLoaded(true);
        } finally {
            setLoading(false);
        }
    };

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2.5}>
                <TextField type="date" fullWidth size="small" label={t('academy.scheduler.select_date')} value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={1.5}>
                <Button variant="contained" color="primary" fullWidth disabled={loading} onClick={handleLoad}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
                    {t('academy.scheduler.load')}
                </Button>
            </Grid>
        </Grid>
    );

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties =>
        ({ border: '1px solid #999', padding: '3px 5px', textAlign: 'left', width, ...extra });

    const methodLabel = (m: string) => m === 'Cash' ? t('academy.print.cash')
        : m === 'Card' ? t('academy.print.card') : t('academy.print.transfer');

    return (
        <GenericPage
            title={t('academy.print.daily_cash')}
            icon={<CashIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
        >
            {!loaded ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                    {t('academy.scheduler.not_loaded')}
                </Typography>
            ) : (
                <>
                    {/* Titre "Rapport de Caisse Journalier — DIM 02 Août" */}
                    <PrintAgencyHeader title={`${capitalizeFirst(t('academy.print.daily_cash'))} — ${formatPrintDay(reportDate)}`} />

                    {/* Totaux par mode de règlement */}
                    <table style={{ width: '60%', minWidth: 320, borderCollapse: 'collapse', fontSize: 11, marginBottom: 12 }}>
                        <tbody>
                            <tr>
                                <td style={cellStyle(180)}>{t('academy.print.cash')}</td>
                                <td style={{ ...cellStyle(), textAlign: 'right', fontWeight: 'bold' }}>{totals.cash}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(180)}>{t('academy.print.card')}</td>
                                <td style={{ ...cellStyle(), textAlign: 'right', fontWeight: 'bold' }}>{totals.card}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(180)}>{t('academy.print.transfer')}</td>
                                <td style={{ ...cellStyle(), textAlign: 'right', fontWeight: 'bold' }}>{totals.transfer}</td>
                            </tr>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <td style={{ ...cellStyle(180), fontWeight: 'bold' }}>{t('academy.print.total')} — {t('academy.print.receipts_count')} : {totals.count}</td>
                                <td style={{ ...cellStyle(), textAlign: 'right', fontWeight: 'bold' }}>{totals.grand}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Détail des encaissements du jour */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={cellStyle(90)}>{t('academy.print.col_receipt')}</th>
                                <th style={cellStyle()}>{t('academy.print.col_student')}</th>
                                <th style={cellStyle(80)}>{t('academy.print.col_method')}</th>
                                <th style={cellStyle(80)}>{t('academy.print.col_amount')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr><td colSpan={4} style={{ ...cellStyle(), textAlign: 'center' }}>{t('academy.print.no_data')}</td></tr>
                            ) : [...rows]
                                .sort((a, b) => String(a.payment_date || '').localeCompare(String(b.payment_date || '')))
                                .map(p => (
                                        <tr key={p.payment_id ?? p.receipt_number}>
                                            <td style={cellStyle(90, { fontWeight: 'bold' })}>{p.receipt_number}</td>
                                            <td style={cellStyle()}>{p.student_name || ''}</td>
                                            <td style={cellStyle(80)}>{methodLabel(p.method)}</td>
                                            <td style={cellStyle(80)}>{p.amount}</td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>
                </>
            )}
        </GenericPage>
    );
};

export default PrintDailyCashPage;
